import { ArrowBack, CheckCircle, Visibility, VisibilityOff } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    IconButton,
    InputAdornment,
    Link as MuiLink,
    TextField,
    Typography,
} from '@mui/material';
import { confirmPasswordReset } from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { auth } from '../firebase';
import { colors } from '../theme';

const ResetPasswordPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // URL'den userRole'ü al (pathname'den veya email'den)
  const getInitialUserRole = () => {
    if (location.pathname.includes('/dietitian/')) return 'dietitian';
    if (location.pathname.includes('/client/')) return 'client';
    return null; // /reset-password için email'den belirlenecek
  };
  
  const [userRole, setUserRole] = useState<'dietitian' | 'client' | null>(getInitialUserRole());

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [oobCode, setOobCode] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    // URL'den action code ve email'i al
    const code = searchParams.get('oobCode');
    const mode = searchParams.get('mode');
    const emailParam = searchParams.get('email');

    if (mode === 'resetPassword' && code) {
      setOobCode(code);
      if (emailParam) {
        setEmail(emailParam);
        
        // Eğer userRole belirlenmemişse, email'e göre backend'den kontrol et
        if (!userRole) {
          checkUserRole(emailParam);
        }
      }
    } else {
      setError('Geçersiz veya eksik şifre sıfırlama linki.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const checkUserRole = async (email: string) => {
    try {
      // Backend'den email'e göre role kontrolü yap
      const { API_ENDPOINTS } = await import('../config/api');
      
      // Önce dietitian kontrolü
      try {
        const dietitianResponse = await fetch(`${API_ENDPOINTS.dietitians}/email/${encodeURIComponent(email)}`);
        if (dietitianResponse.ok) {
          setUserRole('dietitian');
          return;
        }
      } catch (e) {
        // Dietitian değil, devam et
      }
      
      // Client kontrolü
      try {
        const clientResponse = await fetch(`${API_ENDPOINTS.clients}/email/${encodeURIComponent(email)}`);
        if (clientResponse.ok) {
          setUserRole('client');
          return;
        }
      } catch (e) {
        // Client de değil
      }
      
      // Varsayılan olarak client
      setUserRole('client');
    } catch (error) {
      console.error('User role kontrolü hatası:', error);
      // Varsayılan olarak client
      setUserRole('client');
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validations
    if (!newPassword || !confirmPassword) {
      setError('Lütfen tüm alanları doldurun');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Şifreler eşleşmiyor');
      setLoading(false);
      return;
    }

    if (!oobCode) {
      setError('Geçersiz şifre sıfırlama kodu');
      setLoading(false);
      return;
    }

    try {
      // Firebase'de şifreyi sıfırla
      await confirmPasswordReset(auth, oobCode, newPassword);

      setSuccess(true);
      showSuccess(
        'Şifreniz başarıyla sıfırlandı! Giriş sayfasına yönlendiriliyorsunuz...',
        'Başarılı'
      );

      // 2 saniye sonra giriş sayfasına yönlendir
      setTimeout(() => {
        const finalRole = userRole || 'client';
        navigate(`/${finalRole}/login`);
      }, 2000);
    } catch (error: any) {
      console.error('Şifre sıfırlama hatası:', error);
      
      let errorMessage = 'Bir hata oluştu. Lütfen tekrar deneyin.';

      if (error.code === 'auth/expired-action-code') {
        errorMessage = 'Şifre sıfırlama linki süresi dolmuş. Lütfen yeni bir link isteyin.';
      } else if (error.code === 'auth/invalid-action-code') {
        errorMessage = 'Geçersiz şifre sıfırlama kodu. Lütfen yeni bir link isteyin.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Şifre çok zayıf. Lütfen daha güçlü bir şifre seçin.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      showError(errorMessage, 'Şifre Sıfırlama Hatası');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #66BB6A 0%, #43A047 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      {/* Geri Butonu */}
      <IconButton
        onClick={() => {
          const finalRole = userRole || 'client';
          navigate(`/${finalRole}/login`);
        }}
        sx={{
          position: 'fixed',
          top: 20,
          left: 20,
          width: '48px',
          height: '48px',
          zIndex: 1000,
          bgcolor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(30px)',
          color: 'rgba(0, 0, 0, 0.6)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            color: 'rgba(0, 0, 0, 0.8)',
            transform: 'scale(1.05)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
          transition: 'all 0.2s ease',
        }}
      >
        <ArrowBack />
      </IconButton>

      <Container maxWidth="sm">
        <Card elevation={6}>
          <CardContent sx={{ p: 4 }}>
            {/* Başlık */}
            <Box textAlign="center" mb={4}>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Yeni Şifre Belirle
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {email && `E-posta: ${email}`}
              </Typography>
            </Box>

            {/* Success Message */}
            {success && (
              <Alert
                severity="success"
                icon={<CheckCircle />}
                sx={{ mb: 3, animation: 'fadeIn 0.3s ease-in' }}
              >
                <Typography fontWeight={600}>
                  Şifreniz başarıyla sıfırlandı!
                </Typography>
                <Typography variant="body2">
                  Giriş sayfasına yönlendiriliyorsunuz...
                </Typography>
              </Alert>
            )}

            {/* Error Message */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Form */}
            {!success && oobCode && (
              <form onSubmit={handleResetPassword}>
                <TextField
                  fullWidth
                  label="Yeni Şifre"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading}
                  autoFocus
                  helperText="En az 6 karakter"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: colors.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: colors.primary.main,
                        borderWidth: 2,
                      },
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Yeni Şifre (Tekrar)"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: colors.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: colors.primary.main,
                        borderWidth: 2,
                      },
                    },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    mt: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: colors.gradients.primary,
                    '&:hover': {
                      background: colors.gradients.primaryMedium,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {loading ? 'Sıfırlanıyor...' : 'Şifremi Sıfırla'}
                </Button>
              </form>
            )}

            {/* Link geçersizse yeni link iste */}
            {!oobCode && !success && (
              <Box textAlign="center" mt={3}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Şifre sıfırlama linki geçersiz veya eksik.
                </Alert>
                <MuiLink
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate(`/${userRole || 'client'}/forgot-password`)}
                  sx={{
                    color: colors.primary.main,
                    fontWeight: 600,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    background: 'transparent',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: colors.primary.dark,
                      backgroundColor: 'rgba(67, 160, 71, 0.08)',
                      textDecoration: 'none',
                    },
                    '&:active': {
                      backgroundColor: 'rgba(67, 160, 71, 0.12)',
                    },
                  }}
                >
                  Yeni Link İste
                </MuiLink>
              </Box>
            )}

            {/* Alt Bilgi */}
            <Box textAlign="center" mt={3}>
              <Typography variant="body2" color="text.secondary">
                Şifrenizi hatırladınız mı?{' '}
                <MuiLink
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => {
                    const finalRole = userRole || 'client';
                    navigate(`/${finalRole}/login`);
                  }}
                  disabled={loading}
                  sx={{
                    color: colors.primary.main,
                    fontWeight: 600,
                    textDecoration: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    background: 'transparent',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: 1,
                    transition: 'all 0.2s ease',
                    opacity: loading ? 0.6 : 1,
                    '&:hover': {
                      color: colors.primary.dark,
                      backgroundColor: 'rgba(67, 160, 71, 0.08)',
                      textDecoration: 'none',
                    },
                    '&:active': {
                      backgroundColor: 'rgba(67, 160, 71, 0.12)',
                    },
                  }}
                >
                  Giriş Yap
                </MuiLink>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;

